import EmptyState, { EmptyStateSize } from "@galaxy-io/dls/feedback/EmptyState";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import Flex, { FlexDirection } from "@galaxy-io/dls/layout/Flex";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Tree, { type TreeItem, TreeSize } from "@galaxy-io/dls/tree/Tree";
import { styled } from "@linaria/react";
import { ColumnsIcon, DatabaseIcon, TableIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { Input } from "@/components/ui/input";
import { Toolbar } from "@/components/workspace/toolbar";

const Explorer = styled.aside`
  width: 260px;
  max-width: 45vw;
  flex-shrink: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 0.5px solid ${t.color.border.primary};
  background: ${t.color.background.base};
`;

export function SchemaExplorer({
  schema,
  onSelect,
  selected,
}: {
  schema: Record<string, string[]>;
  onSelect: (name: string) => void;
  selected?: string | null;
}) {
  const { params, update } = useUrlState();
  const [collapsedNamespaces, setCollapsedNamespaces] = useState<string[]>([]);
  const search = params.get("schemaQ") || "";
  const expanded = (params.get("tables") || "").split(",").filter(Boolean);
  const namespaces = [...new Set(Object.keys(schema).map((name) => name.split(".")[0]))];
  const items: TreeItem[] = namespaces.flatMap((namespace) => {
    const tables = Object.entries(schema).filter(
      ([name, columns]) =>
        name.startsWith(`${namespace}.`) &&
        [name, ...columns].some((value) => value.toLowerCase().includes(search.toLowerCase())),
    );
    return tables.length
      ? [
          {
            id: `namespace:${namespace}`,
            label: namespace,
            icon: DatabaseIcon,
            children: tables.map(([name, columns]) => ({
              id: name,
              label: name.split(".").slice(1).join("."),
              icon: TableIcon,
              children: columns.map((column) => ({
                id: `column:${name}:${column}`,
                label: column,
                icon: ColumnsIcon,
              })),
            })),
          },
        ]
      : [];
  });
  return (
    <Explorer aria-label="Schema explorer">
      <Toolbar>
        <Input
          aria-label="Search tables"
          placeholder="Find a table…"
          value={search}
          onChange={(value) => update({ schemaQ: value || null }, true)}
          type="search"
          size={InputSize.SMALL}
          label="Search tables"
          hideLabel
        />
      </Toolbar>
      <Flex direction={FlexDirection.COLUMN} overflow="auto" minHeight={0} grow={1} padding={8}>
        {items.length ? (
          <Tree
            ariaLabel="Database tables"
            size={TreeSize.SMALL}
            items={items}
            value={selected ?? params.get("dataset")}
            expandedIds={[
              ...namespaces
                .filter((name) => !collapsedNamespaces.includes(name))
                .map((name) => `namespace:${name}`),
              ...expanded,
            ]}
            onExpandedIdsChange={(ids) => {
              setCollapsedNamespaces(
                namespaces.filter((name) => !ids.includes(`namespace:${name}`)),
              );
              update({ tables: ids.filter((id) => Object.hasOwn(schema, id)).join(",") || null });
            }}
            onChange={(id) => {
              if (Object.hasOwn(schema, id)) onSelect(id);
            }}
          />
        ) : (
          <EmptyState
            size={EmptyStateSize.SMALL}
            header="No matching tables"
            description="Try another table or column name."
          />
        )}
      </Flex>
    </Explorer>
  );
}
